import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  memberAreasService,
  Banner,
  CustomMenuItem,
  MemberArea,
  PlatformSettingsUpdate,
  ApplyMenuToAllAreasInput,
} from '@/services/member-areas.service';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/auth';
import { isPlatformAdmin } from '@/lib/member-home';

export function useAccessibleMemberAreas(enabled = true) {
  const { user, isAuthenticated } = useAuthStore();
  const canRead = enabled && isAuthenticated && !!user?.id;
  const admin = isPlatformAdmin(user?.role);
  const query = useQuery({
    queryKey: [
      'memberAreas',
      'accessible',
      admin ? 'admin' : 'my',
      user?.id,
      user?.role,
    ],
    queryFn: () => {
      if (!canRead) throw new Error('Sessão necessária para consultar áreas.');
      return admin
        ? memberAreasService.getAllAdminMemberAreas()
        : memberAreasService.getMyMemberAreas();
    },
    enabled: canRead,
    retry: false,
  });
  // A failed authorization/refetch must not keep privileged stale data visible.
  return { ...query, data: canRead && !query.isError ? query.data : undefined };
}

export function useMyMemberAreas(enabled = true) {
  return useQuery({
    queryKey: ['memberAreas', 'my'],
    queryFn: () => memberAreasService.getMyMemberAreas(),
    enabled,
  });
}

// ==================== BANNERS ====================

export const memberAreaProductsKey = ['memberAreas', 'products'] as const;

/** Produtos (áreas) na ordem da hierarquia, para o admin. */
export function useMemberAreaProducts(enabled = true) {
  return useQuery({
    queryKey: memberAreaProductsKey,
    queryFn: () => memberAreasService.listProducts(),
    enabled,
  });
}

function useInvalidateMemberAreas() {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: ['memberAreas'] });
}

export function useReorderMemberAreas() {
  const invalidate = useInvalidateMemberAreas();
  return useMutation({
    mutationFn: (ids: string[]) => memberAreasService.reorder(ids),
    onSettled: invalidate,
  });
}

export function useSaveMemberArea() {
  const invalidate = useInvalidateMemberAreas();
  return useMutation({
    mutationFn: ({ id, data }: { id?: string; data: Partial<MemberArea> }) =>
      id ? memberAreasService.update(id, data) : memberAreasService.create(data),
    onSuccess: invalidate,
  });
}

export function useBanners() {
  return useQuery({
    queryKey: ['banners', 'admin'],
    queryFn: () => memberAreasService.getBanners(),
  });
}

export function usePublicBanners(position?: string) {
  return useQuery({
    queryKey: ['banners', 'public', position],
    queryFn: () => memberAreasService.getPublicBanners(position),
  });
}

export function useCreateBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<Banner> & { order: number }) =>
      memberAreasService.createBanner(
        data as Omit<Banner, 'id' | 'createdAt' | 'updatedAt'>,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success('Banner criado com sucesso!');
    },
    onError: () => {
      toast.error('Erro ao criar banner');
    },
  });
}

export function useUpdateBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Banner> }) =>
      memberAreasService.updateBanner(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success('Banner atualizado!');
    },
    onError: () => {
      toast.error('Erro ao atualizar banner');
    },
  });
}

export function useDeleteBanner() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => memberAreasService.deleteBanner(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
      toast.success('Banner excluído!');
    },
    onError: () => {
      toast.error('Erro ao excluir banner');
    },
  });
}

export function useReorderBanners() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => memberAreasService.reorderBanners(ids),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['banners'] });
    },
  });
}

// ==================== MENU ITEMS ====================

export function useMenuItems(memberAreaId?: string) {
  const { user, isAuthenticated } = useAuthStore();
  const canRead = isAuthenticated && isPlatformAdmin(user?.role);
  const query = useQuery({
    queryKey: ['menuItems', 'admin', memberAreaId ?? 'global', user?.id, user?.role],
    queryFn: () => memberAreasService.getMenuItems(memberAreaId),
    enabled: canRead,
    retry: false,
  });
  return { ...query, data: canRead && !query.isError ? query.data : undefined };
}

export function usePublicMenuItems(memberAreaId?: string, enabled = true) {
  const { user, isAuthenticated } = useAuthStore();
  const canRead = enabled && isAuthenticated && !!user?.id;
  const query = useQuery({
    queryKey: ['menuItems', 'public', memberAreaId ?? 'global', user?.id, user?.role],
    queryFn: () => memberAreasService.getPublicMenuItems(memberAreaId),
    enabled: canRead,
    retry: false,
  });
  return { ...query, data: canRead && !query.isError ? query.data : undefined };
}

export function useCreateMenuItem(memberAreaId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: Partial<CustomMenuItem> & { order: number }) =>
      memberAreasService.createMenuItem(
        data as Omit<CustomMenuItem, 'id' | 'createdAt' | 'updatedAt'>,
        memberAreaId,
      ),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success('Item de menu criado!');
    },
    onError: () => {
      toast.error('Erro ao criar item de menu');
    },
  });
}

export function useUpdateMenuItem(memberAreaId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<CustomMenuItem> }) =>
      memberAreasService.updateMenuItem(id, data, memberAreaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success('Item de menu atualizado!');
    },
    onError: () => {
      toast.error('Erro ao atualizar item de menu');
    },
  });
}

export function useDeleteMenuItem(memberAreaId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => memberAreasService.deleteMenuItem(id, memberAreaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success('Item de menu excluído!');
    },
    onError: () => {
      toast.error('Erro ao excluir item de menu');
    },
  });
}

export function useReorderMenuItems(memberAreaId?: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids: string[]) => memberAreasService.reorderMenuItems(ids, memberAreaId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['menuItems'] });
    },
    onError: () => toast.error('Não foi possível reordenar o menu.'),
  });
}

export const applyMenuToAllAreasKey = ['menuItems', 'apply-to-all'] as const;

export function useApplyMenuToAllAreas() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: applyMenuToAllAreasKey,
    mutationFn: (source: ApplyMenuToAllAreasInput) => memberAreasService.applyMenuToAllAreas(source),
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: ['menuItems'] });
      toast.success(`Menu aplicado a ${result.areasUpdated} áreas (${result.itemsPerArea} itens por área).`);
    },
    onError: () => toast.error('Não foi possível confirmar a aplicação do menu às áreas.'),
  });
}

// ==================== PLATFORM SETTINGS ====================

export function usePlatformSettings() {
  return useQuery({
    queryKey: ['platformSettings'],
    queryFn: () => memberAreasService.getPlatformSettings(),
  });
}

export function useUpdatePlatformSettings() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: PlatformSettingsUpdate) =>
      memberAreasService.updatePlatformSettings(data),
    onSuccess: (settings) => {
      queryClient.setQueryData(['platformSettings'], settings);
      queryClient.invalidateQueries({ queryKey: ['platformSettings'] });
      toast.success('Configurações atualizadas!');
    },
    onError: () => {
      toast.error('Erro ao atualizar configurações');
    },
  });
}
